<template>
  <div
    :class="orientation"
    class="search-filter-group">
    <ul v-if="groupFilters != null">
      <li
        v-for="filter in groupFilters"
        :key="filter.name">
        <div
          :class="getFilterOpenClass( filter )"
          class="filter">
          <div
            v-show="isFilterOpen( filter )"
            class="filter-body">
            <div class="filter-content">
              <component
                :is="filterTemplates[formatFilterType( filter.type )]"
                v-model="filter.value"
                :filter="filter.alias"
                :name="filter.name"
                :config="filter.config"
                :group="filter.group"
                :options="filter.options"></component>
            </div>
          </div>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { camelCase } from "lodash";
import { mapState } from "vuex";
import { TweenMax } from "gsap";

export default {
  props: {
    additionalProperties: {
      type: Array,
      default: () => []
    },
    orientation: {
      type: String,
      default: "vertical",
      validator: ( value ) => {
        let isValid;

        switch ( value ) {
          case "horizontal":
          case "vertical":
            isValid = true;
            break;
          default:
            isValid = false;
        }

        return isValid;
      }
    },
    filterGroups: {
      type: Array,
      default: () => ["default"]
    },
    collapsible: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      openFilters: [],
      filterTemplates: {
        textbox: () => import( "~/Components/Search/Filters/Textbox.vue" ),
        checkboxes: () => import( "~/Components/Search/Filters/Checkboxes.vue" ),
        radiobuttons: () => import( "~/Components/Search/Filters/Radiobuttons.vue" ),
        letters: () => import( "~/Components/Search/Filters/Letters.vue" ),
        slider: () => import( "~/Components/Search/Filters/Slider.vue" ),
        dropdown: () => import( "~/Components/Search/Filters/Dropdown.vue" ),
        datepicker: () => import( "~/Components/Search/Filters/Datepicker.vue" ),
        tabs: () => import( "~/Components/Search/Filters/Tabs.vue" ),
        tagCheckboxes: () => import( "~/Components/Search/Filters/TagCheckboxes.vue" )
      }
    };
  },
  computed: {
    groupFilters () {
      let result;

      if ( this.filters.length > 0 ) {
        result = this.filters.filter(
          ( x ) => this.filterGroups.includes( x.group )
        );
      } else {
        result = [];
      }
      return result;
    },
    ...mapState( {
      filters: state => state.search.filters,
      openAllFilters: state => state.search.openAllFilters
    } )
  },
  created () {
    if ( this.collapsible && this.groupFilters.length > 0 ) {
      this.groupFilters.forEach( ( el ) => {
        // HACK: short circuited this to true to show all filters for now
        // if ( this.openAllFilters || index === 0 || el.value ) {
        this.openFilters.push( el.alias );
        // }
      } );
    }
  },
  methods: {
    formatFilterType ( filterType ) {
      console.log( filterType );
      let formattedFilterType = filterType || "";

      formattedFilterType = formattedFilterType.replace( /\s/g, "" );

      return camelCase( formattedFilterType );
    },
    isFilterOpen ( filter ) {
      let result = true;

      if ( this.collapsible ) {
        if ( !this.openFilters.some( x => x === filter.alias ) ) {
          result = false;
        }
      }

      return result;
    },
    getFilterOpenClass ( filter ) {
      let result = "";

      if ( this.isFilterOpen( filter ) ) {
        result = "open";
      }

      return result;
    },
    collapsibleIndicatorRotation ( filter ) {
      let result = 0;

      if ( this.collapsible ) {
        if ( this.openFilters.some( x => x === filter.alias ) ) {
          result = 180;
        }
      }

      return result;
    },
    toggleFilter ( filter ) {
      if ( this.collapsible ) {
        const openFilter = this.openFilters.find( x => x === filter.alias );

        if ( openFilter ) {
          this.openFilters.splice( this.openFilters.indexOf( openFilter ), 1 );
        } else {
          this.openFilters.push( filter.alias );
        }
      }
    },
    beforeEnter ( el ) {
      el.style.height = "auto";
      el.style.overflow = "hidden";
    },
    enter ( el, done ) {
      TweenMax.from(
        el,
        0.2,
        {
          height: "0",
          onComplete: () => {
            el.style.overflow = "auto";
            done();
          }
        }
      );
    },
    beforeLeave ( el ) {
      el.style.overflow = "hidden";
    },
    leave ( el, done ) {
      TweenMax.to(
        el,
        0.2,
        {
          height: 0,
          onComplete: () => {
            el.style.overflow = "auto";
            done();
          }
        }
      );
    }
  }
};
</script>

<style lang="less" scoped>
.search-filter-group {
  ul {
    margin: 0;
    padding: 0;
    list-style-type: none;
  }

  .filter {
    position: relative;

    .filter-header {
      position: relative;
      margin: 0;
      padding: 10px;

      h4 {
        margin: 0;
      }

      .collapse-indicator {
        display: flex;
        align-items: center;
        position: absolute;
        height: 100%;
        right: 20px;
        top: 0;
      }
    }

    .filter-body {
      position: relative;

      .filter-content {
        padding: 10px;
      }
    }
  }

  &.horizontal {
    width: 100%;

    ul {
      display: flex;
      flex-flow: wrap;

      @media ( max-width: 991px ) {
        flex-direction: column;

        .filter-content {
          padding: 0 !important;
        }
      }

      li {
        margin: 4px;

        @media ( max-width: 991px ) {
          flex-grow: 1;
          margin: 4px auto;
        }

        &:last-child {
          .filter {
            margin-right: 0;

            @media ( max-width: 991px ) {
              margin-bottom: 0;
            }
          }
        }
      }
    }

    .filter {
      margin-bottom: 0;
      margin-right: 5px;
      position: relative;
      display: flex;

      @media ( max-width: 991px ) {
        margin-right: 0;
        margin-bottom: 10px;
      }

      .filter-header {
        background: none;
        padding-left: 0;
      }

      .filter-body {
        flex-grow: 1;
        padding-left: 0;
        width: 100%;
      }

      &:hover {
        .filter-body {
          max-height: 300px;
          -webkit-overflow-scrolling: touch;
        }
      }

    }
  }
}
</style>
